import Script from 'next/script';
import '@/styles/global.css';
import '@public/countdownTimer.css';
import Layout from '@components/main/layout';
import CombinedProvider from '@context/combinedProvider';
import { NextUIProvider } from '@nextui-org/react';
import { NextPageContext } from 'next';
import { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { ThemeProvider as NextThemesProvider } from 'next-themes';
import { useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import { useAmplitudePageTracking } from '@/hooks/useAmplitudePageTracking';
import * as gtag from '@/lib/google/gtag';

export function MyApp({ Component, pageProps, initialContext }: AppProps & { initialContext: string }) {
	const router = useRouter();

	const ZOHO_WIDGET_CODE = process.env.NEXT_PUBLIC_ZOHO_WIDGET_CODE;

	useEffect(() => {
		const handleRouteChange = (url: string) => {
			gtag.pageview(url);
		};
		router.events.on('routeChangeComplete', handleRouteChange);
		return () => {
			router.events.off('routeChangeComplete', handleRouteChange);
		};
	}, [router.events]);

	useAmplitudePageTracking();

	return (
		<>
			<NextUIProvider navigate={router.push}>
				<NextThemesProvider defaultTheme='light'>
					<ToastContainer />
					<CombinedProvider initialContext={initialContext}>
						<Layout>
							<Component {...pageProps} />
						</Layout>
					</CombinedProvider>
				</NextThemesProvider>
			</NextUIProvider>
			{/* Global Site Tag (gtag.js) - Google Analytics */}
			<Script strategy='afterInteractive' src={`https://www.googletagmanager.com/gtag/js?id=${gtag.GA_TRACKING_ID}`} />
			<Script id='ga-script' strategy='afterInteractive'>
				{`
                        window.dataLayer = window.dataLayer || [];
                        function gtag(){dataLayer.push(arguments);}
                        gtag('js', new Date());
                        gtag('config', '${gtag.GA_TRACKING_ID}', {
                            'cookie_flags': 'samesite=none;secure'
                        });
                    `}
			</Script>
			<Script id='zoho-salesiq-script' strategy='afterInteractive'>
				{`
                    var $zoho = $zoho || {};$zoho.salesiq = $zoho.salesiq || {
                      widgetcode: "${ZOHO_WIDGET_CODE}",
                      values: {},
                      ready: function() {}
                    };
                    var d = document;
                    var s = d.createElement("script");
                    s.type = "text/javascript";
                    s.id = "zsiqscript";
                    s.defer = true;
                    s.src = "https://salesiq.zohopublic.eu/widget";
                    var t = d.getElementsByTagName("script")[0];
                    t.parentNode.insertBefore(s, t);
                  `}
			</Script>
		</>
	);
}

MyApp.getInitialProps = async ({ ctx }: { ctx: NextPageContext }) => {
	const { query } = ctx;
	const instance = query.instance ?? '';

	return { initialContext: instance };
};

export default MyApp;
